/**
 * №29 (S1) — the work rail's RECEIPTS tab. Mockup D's "PRODUCED SO FAR", listed
 * *"while the run continues"*.
 *
 * A receipt is a verb that ran and the thing it touched — never a summary of intent.
 * That distinction is №22.4's whole D-HM1 contract ("no tick without receipts"), and
 * this tab is that contract given a permanent home beside the conversation instead of
 * being buried inside one run bubble.
 *
 * Two sources, one grammar:
 *
 *  - a focused RUN → its own thread (`get_task_thread`), where each run message
 *    carries `receipts: [{verb, ref}]` and each answer carries `answer_events` whose
 *    `action` lines are the verbs the answer executed. Oldest first, so new ones land
 *    at the bottom as the run continues.
 *  - no focused run → the anchor's rollup (`get_anchor_stream`), one row per RUN,
 *    with its action count and how it ended. Clicking a row focuses that run in the
 *    conversation (D-CH4: retarget, never navigate).
 *
 * Nothing is derived beyond ordering. A run with no receipts renders as a run with no
 * receipts — which, per D-HM1, is itself the information.
 */
import { useTaskThread } from "../../../hooks/use-task-thread";
import { useAnchorStream } from "../../../hooks/use-anchor-stream";
import { useAppScope } from "../../../hooks/use-app-scope";
import { useCoPanel } from "../../collab/co-panel-context";
import { RailEmpty, RailHead, RailLoading } from "./RailPrimitives";
import type { RailBinding } from "./rail-binding";
import type { StreamEvent, TaskThreadMessage } from "../../../../lib/types";

interface Receipt {
  verb: string;
  ref: string | null;
  summary: string | null;
}

/** Every receipt this run has written, in the order it wrote them. */
function receiptsOf(messages: TaskThreadMessage[]): Receipt[] {
  const out: Receipt[] = [];
  for (const m of messages) {
    for (const r of m.metadata?.receipts ?? []) {
      out.push({ verb: r.verb, ref: r.ref ?? null, summary: r.summary ?? null });
    }
    for (const e of m.metadata?.answer_events ?? []) {
      if (e.kind === "action") out.push({ verb: e.tool ?? e.text, ref: null, summary: e.text });
    }
  }
  return out;
}

function timeOf(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function RunRow({ event, onOpen }: { event: StreamEvent; onOpen: () => void }) {
  const actions = event.actionCount ?? 0;
  return (
    <button type="button" className="railrun" onClick={onOpen} data-testid="rail-receipt-run">
      <span className="railrun__t">{event.title}</span>
      <span className="railrun__m">
        {event.actor.name ?? "—"} · {actions} action{actions === 1 ? "" : "s"} · {timeOf(event.ts)}
      </span>
      {event.termination === "blocked_honest" && <span className="railrun__end">stopped and said what it lacked</span>}
      {event.termination === "needs_you" && <span className="railrun__end">stopped for you</span>}
      {event.termination == null && <span className="railrun__end">no recorded outcome</span>}
    </button>
  );
}

export function RailReceipts({ binding }: { binding: RailBinding }) {
  const panel = useCoPanel();
  const { anchor } = useAppScope();
  const thread = useTaskThread(binding.taskId ?? undefined, !!binding.taskId);
  // Shares react-query's cache entry with the conversation's own subscription, so
  // reading it here costs no extra request (D-CV11 — nothing double-subscribes).
  const stream = useAnchorStream(anchor, { live: false });

  if (binding.taskId) {
    if (thread.isLoading) return <RailLoading />;
    const receipts = receiptsOf(thread.data?.conversation?.messages ?? []);
    if (receipts.length === 0) {
      return (
        <RailEmpty>
          This run has produced nothing yet. Receipts land here as verbs run — nothing is listed
          until something has actually happened.
        </RailEmpty>
      );
    }
    return (
      <div className="railpane" data-testid="rail-receipts">
        <RailHead>
          Produced so far · {receipts.length} receipt{receipts.length === 1 ? "" : "s"}
        </RailHead>
        {receipts.map((r, i) => (
          <div key={`${r.verb}-${i}`} className="railreceipt" data-testid="rail-receipt" title={r.summary ?? r.verb}>
            <span className="railreceipt__verb">{r.verb}</span>
            {r.ref && <span className="railreceipt__ref">{r.ref}</span>}
          </div>
        ))}
      </div>
    );
  }

  const runs = (stream.data?.events ?? []).filter((e) => e.kind === "run");
  if (stream.isLoading) return <RailLoading />;
  if (runs.length === 0) {
    return (
      <RailEmpty>
        Nothing has run under <b>{binding.label}</b> yet. When something does, what it produced is
        listed here while it is still going.
      </RailEmpty>
    );
  }
  return (
    <div className="railpane" data-testid="rail-receipts">
      <RailHead>
        {runs.length} run{runs.length === 1 ? "" : "s"} under {binding.label}
      </RailHead>
      {runs.map((e) => (
        <RunRow
          key={e.id}
          event={e}
          onOpen={() => {
            if (e.taskId) panel.focusTask({ taskId: e.taskId, agentId: e.actor.id ?? undefined, agentName: e.actor.name ?? undefined, label: e.anchorLabel ?? undefined });
          }}
        />
      ))}
    </div>
  );
}
